import React, { useState, useEffect } from "react";
import Switch from "react-switch";
import { useQuery, useMutation } from "@apollo/client";
import useSettings from "../../../../hooks/settings";
import { Settings } from "../../../../models/settings_model";
import { ShippingCostInput } from "../../../../models/shipping_cost_model";
import {
  GET_SHIPPING_COSTS,
  ADD_SHIPPING_COST,
  UPDATE_SHIPPING_COST,
  DELETE_SHIPPING_COST,
} from "../queries";

const Delivery: React.FC = () => {
  const [settings, saveSettings] = useSettings();
  const [shippingCosts, setShippingCosts] = useState<ShippingCostInput[]>([]);
  const [alertMessage, setAlertMessage] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const {
    data: shippingCostsData,
    loading,
    refetch,
  } = useQuery(GET_SHIPPING_COSTS);
  const [addShippingCost] = useMutation(ADD_SHIPPING_COST);
  const [updateShippingCost] = useMutation(UPDATE_SHIPPING_COST);
  const [deleteShippingCost] = useMutation(DELETE_SHIPPING_COST);

  useEffect(() => {
    if (shippingCostsData?.getShippingCosts) {
      setShippingCosts(shippingCostsData.getShippingCosts);
    }
  }, [shippingCostsData]);

  const updateSetting = (
    checked: boolean | string,
    setting_type: keyof Settings
  ) => {
    saveSettings(setting_type, checked);
  };

  const handleAddShippingCost = () => {
    setShippingCosts([
      ...shippingCosts,
      { price: 0, distance: 0, status: true },
    ]);
  };

  const handleUpdateShippingCost = (
    index: number,
    field: keyof ShippingCostInput,
    value: any
  ) => {
    const updatedCosts = [...shippingCosts];
    updatedCosts[index] = { ...updatedCosts[index], [field]: value };
    setShippingCosts(updatedCosts);
  };

  const handleDeleteShippingCost = async (index: number) => {
    const cost = shippingCosts[index];
    if (cost.id) {
      try {
        await deleteShippingCost({ variables: { id: cost.id } });
        await refetch();
        setAlertMessage({
          type: "success",
          message: "Costo de envío eliminado exitosamente",
        });
      } catch (error) {
        console.error("Error deleting shipping cost:", error);
        setAlertMessage({
          type: "danger",
          message: "Error al eliminar el costo de envío",
        });
      }
    } else {
      const updatedCosts = shippingCosts.filter((_, i) => i !== index);
      setShippingCosts(updatedCosts);
    }
  };

  const handleSaveShippingCosts = async () => {
    try {
      for (let i = 0; i < shippingCosts.length; i++) {
        const cost = shippingCosts[i];
        if (cost.price > 0 && cost.distance > 0) {
          const cleanCost = cleanShippingCostData(cost);

          if (cost.id) {
            await updateShippingCost({ variables: { data: cleanCost } });
          } else {
            await addShippingCost({ variables: { data: cleanCost } });
          }
        }
      }
      await refetch();
      setAlertMessage({
        type: "success",
        message: "Costos de envío guardados exitosamente",
      });
    } catch (error) {
      console.error("Error saving shipping costs:", error);
      setAlertMessage({
        type: "danger",
        message: "Error al guardar los costos de envío",
      });
    }
  };

  const clearAlert = () => {
    setAlertMessage(null);
  };

  const cleanShippingCostData = (cost: ShippingCostInput) => {
    return {
      id: cost.id,
      price: cost.price,
      distance: cost.distance,
      status: cost.status,
    };
  };

  return (
    <div className="row justify-content-start mt-3">
      <div className="col col-md-6">
        <div className="card mb-2">
          <div className="card-body ">
            <h4 className="">Envíos a domicilio</h4>
            <div className="form-group p_relative">
              <label htmlFor="product_categories ">
                <small className="text-muted">
                  Deja que tus clientes sepan si haces envíos a domicilio desde
                  Foo
                </small>
                <div className="ml-2 row justify-content-start align-items-center my-2">
                  <Switch
                    checked={settings.disable_delivery ? false : true}
                    onChange={(checked) =>
                      updateSetting(!checked, "disable_delivery")
                    }
                  />
                  <div className="ml-1">
                    {settings.disable_delivery
                      ? "No hago envíos a domicilio"
                      : "Hago envíos a domicilio"}
                  </div>
                </div>
              </label>
            </div>
            <div id="shipping_btn" className="mb-3">
              <h4 className="">Opciones</h4>
              <div className="form-group p_relative">
                <label htmlFor="product_categories ">
                  <small className="text-muted">
                    Las direcciones y costos de envíos son calculados con el
                    apoyo de Google Maps
                  </small>
                  <div className="ml-2 row justify-content-start align-items-center my-2">
                    <Switch
                      checked={settings.google_maps ? true : false}
                      onChange={(checked) =>
                        updateSetting(checked, "google_maps")
                      }
                    />
                    <div className="ml-1">
                      Apoyo de Google Maps para direcciones
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!settings.disable_delivery && (
        <div className="col">
          <div className="card mb-2 " id="shipping_costs">
            {alertMessage && (
              <div
                className={`alert alert-${alertMessage.type} alert-dismissible fade show m-3`}
                role="alert"
              >
                {alertMessage.message}
                <button
                  type="button"
                  className="close"
                  onClick={clearAlert}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            )}
            <div className="card-body">
              <h4>Costos de Envío</h4>
              <p>
                Puedes agregar tus propios costos de envío dentro de Foo en caso
                de que así los manejes.
              </p>
              <p>
                Los costos de envío se calculan dentro de la aplicación por
                distancia, así que debes cargar dentro de tu panel de
                preferencias los costos por rangos en distancia.
              </p>
            </div>
            <div id="delivery_costs" className="p-3">
              {loading ? (
                <div className="text-center py-3">
                  <i className="fas fa-spinner fa-spin"></i> Cargando...
                </div>
              ) : (
                <>
                  {shippingCosts.map((cost, index) => (
                    <div key={cost.id || index} className="form-row mb-2">
                      <div className="col-5">
                        <label className="text-muted">Costo</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={cost.price || ""}
                          onChange={(e) =>
                            handleUpdateShippingCost(
                              index,
                              "price",
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />
                      </div>
                      <div className="col-5">
                        <label className="text-muted">Hasta (km)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={cost.distance || ""}
                          onChange={(e) =>
                            handleUpdateShippingCost(
                              index,
                              "distance",
                              parseInt(e.target.value) || 0
                            )
                          }
                        />
                      </div>
                      <div className="col-2">
                        <label className="text-white">Acciones</label>
                        <button
                          type="button"
                          className="btn p-0 py-1 m-0"
                          onClick={() => handleDeleteShippingCost(index)}
                        >
                          <i className="far fa-minus-square fa-2x text-danger"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="form-row">
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddShippingCost}
                      >
                        <i className="fas fa-plus"></i> Agregar Rango
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              className="btn btn-success btn-block mt-4"
              onClick={handleSaveShippingCosts}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delivery;
